import { html, css } from "lit";
import { customElement, property, query, queryAll } from "lit/decorators.js";
import { ModuleCode } from "@reporting/rp-types/accounting";
import { msg } from "@omegagrid/localize";
import { map } from "lit/directives/map.js";
import { ContextFilter, ContextFilterItem, PeriodDimensionType } from "@reporting/rp-types/analytics";
import { DimensionDefinition } from "@reporting/rp-types/definition";
import { Button, dom, events } from "@omegagrid/core";
import { TreeAdapter, TreeDropdown, TreeEvent, TreeMenu } from "@omegagrid/tree";
import { BaseSection } from "./sectionBase";
import { createRef, ref } from "lit/directives/ref.js";
import { getContextYears, isPeriodDimension, mergeDynamicPeriodFilters } from "../../common";
import { CommonPeriodAdapter, PeriodAdapter } from "../../adapters";
import { PeriodParsingOptions } from "../../model";
import { DynamicPeriod } from "./dynamicPeriod";

interface PeriodFilterRow {
	filter: ContextFilter;
	filterIndex: number;
	/** index into filter.items — 'PX' filters render one row per dynamic period item */
	itemIndex?: number;
}

@customElement('rp-fsection-period')
export class PeriodSection extends BaseSection {

	static styles = [...BaseSection.styles, css`
		og-button.add {
			border: 1px dashed var(--og-accent-color);
			width: 100%;
		}

		.row {
			display: flex;
			align-items: center;
		}

		.row og-tree-dropdown, .row .date-input, .row .dynamicperiod {
			flex: 1;
			border: 1px solid var(--og-colortype-accent-border-color);
			background-color: var(--og-accent-color-alpha-30);
			color: var(--og-text-color-3);
		}

		.date-input {
			border-radius: var(--og-base-radius);
			display: flex;
			flex-direction: row;
			align-items: center;
			overflow: hidden;
		}

		.date-input .date-input-label {
			flex: 0;
			white-space: nowrap;
			color: var(--og-text-color-2);
			opacity: 0.7;
			padding: 0 4px;
		}

		.date-input og-calendar-dropdown {
			height: 20px;
			border-radius: 0;
			flex: 1;
		}

		.row .dynamicperiod {
			border-radius: var(--og-base-radius);
			display: flex;
			flex-direction: row;
			align-items: center;
		}

		.row .dynamicperiod og-button {
			flex: 0 0 20px;
		}

		rp-filters-dynamicperiod {
			flex: 1;
			border-right: 1px solid var(--og-colortype-accent-border-color);
		}
	`];

	@query('og-button.add')
	addButton!: Button;

	@queryAll('og-button.remove')
	removeButtons!: NodeListOf<Button>;

	@property({type: Array})
	periods: DimensionDefinition[];

	@query('.dropdown-period-types')
	dropdownPeriodTypes: TreeDropdown;

	@queryAll('rp-filters-dynamicperiod')
	dynamicPeriodsFilters!: NodeListOf<DynamicPeriod>;

	

	private addMenuRef = createRef<TreeMenu>();
	get addMenu() { return this.addMenuRef?.value }

	private _filters: ContextFilter[];
	get filters() {
		if (this._filters) return this._filters;
		if (!this.context) return [];
		this._filters = mergeDynamicPeriodFilters(
			this.context.filters?.filter(f => isPeriodDimension(f.dim)) as ContextFilter[] ?? []
		);
		return this._filters;
	}

	get rows(): PeriodFilterRow[] {
		return this.filters.flatMap((filter, filterIndex) => {
			if (filter.dim != 'PX') return [{filter, filterIndex}];
			const items = this.getFilterItems(filter);
			return items.length
				? items.map((_, itemIndex) => ({filter, filterIndex, itemIndex}))
				: [{filter, filterIndex, itemIndex: 0}];
		});
	}

	getFilterItems(filter: ContextFilter): (ContextFilterItem|string)[] {
		return typeof filter.items === 'string' ? [filter.items] : filter.items ?? [];
	}

	private _periodDimensionMap: Map<string, DimensionDefinition>;
	get periodDimensionMap() {
		if (!this._periodDimensionMap) this._periodDimensionMap = new Map((this.periods || []).map(dim => [dim.dim_num, dim]));
		return this._periodDimensionMap;
	}

	private adapters = new Map<PeriodDimensionType, TreeAdapter>();
	getAdapter(type: PeriodDimensionType) {
		if (!this.adapters.has(type) && this.service && this.context?.module) {
			this.adapters.set(type, type == 'PC'
				? new CommonPeriodAdapter(this.context.module as ModuleCode)
				: new PeriodAdapter(this.service, this.context.module as ModuleCode, type)
			);
		}
		return this.adapters.get(type);
	}

	get addMenuItems() {
		return this.periods.map(period => ({
			k: period.dim_num,
			v: period.description,
			level: 0,
			collapsed: true
		}));
	}

	willUpdate(props: Map<PropertyKey, unknown>) {
		super.willUpdate(props);
		if (props.has('module') && this.module) {
			this.adapters.clear();
		}
		this._filters = null;
	}

	updated() {
		this.addMenu?.setSourceData(this.addMenuItems, {
			selectMode: 'single',
			checkboxes: false
		});

		if (this.dropdownPeriodTypes) {
			this.dropdownPeriodTypes.setSourceData(this.module.period_types.map(pt => ({
				k: pt.id.toString(),
				v: msg(pt.name),
			})), {selectMode: 'single', checkboxes: false, autoClose: true});
		}
	}

	disconnectedCallback(): void {
		this.addMenu?.remove();
	}

	_onFilterChange = (e: TreeEvent, row: PeriodFilterRow, rowIndex: number) => {
		const filter = row.filter;
		filter.items = e.component.selection.serialize().map(item => item[0].toString());
		if (filter.items.length == 0) dom.showElement(this.removeButtons[rowIndex]);
		else dom.hideElement(this.removeButtons[rowIndex]);
		this.updateFilters(false);
	}

	_onDynamicFilterChange = (e: events.ChangeEvent, row: PeriodFilterRow) => {
		const items = this.getFilterItems(row.filter);
		if (e.value) items[row.itemIndex] = e.value;
		else items.splice(row.itemIndex, 1);
		row.filter.items = items as ContextFilterItem[]|string[];
		this.updateFilters(!e.value);
	}

	openAddMenu() {
		dom.setSize(this.addMenu, {w: this.addButton.offsetWidth});
		this.addMenu.open(this.addButton);
	}

	updateFilters(refresh?: boolean) {
		this.updateContext('filters', [
			...mergeDynamicPeriodFilters(this.filters),
			...(this.context.filters || []).filter(f => !isPeriodDimension(f.dim)) as ContextFilter[],
		]);
		if (refresh) this.requestUpdate();
	}

	addFilter(dim: string) {
		// const filter = this.filters.find(f => f.dim == e.node.key as string);
		this.filters.push({dim: dim as string, items: []});
		this.updateFilters(true);
	}

	removeFilter(row: PeriodFilterRow) {
		const items = this.getFilterItems(row.filter);
		if (row.filter.dim == 'PX' && items.length > 1) {
			items.splice(row.itemIndex, 1);
			row.filter.items = items as ContextFilterItem[]|string[];
		} else {
			this.filters.splice(row.filterIndex, 1);
		}
		this.updateFilters(true);
	}

	normalizeFilterItems(items: (ContextFilterItem|string)[]) {
		return items?.map(item => Array.isArray(item) ? item : [(item ?? '').toString(), 0, 1]);
	}

	_onPeriodTypeChange = (e: TreeEvent) => {
		if (!e.node?.key) return;
		this.updateContext('period_type', Number(e.node.key));
	}

	_onPeriodBaseDateChange = (e: events.ChangeEvent<Date>) => {
		const value = e.value ? e.value.toISOString().substring(0, 10) : null;
		this.updateContext('base_date', value);
		const periodParsingOptions = this.periodParsingOptions;
		this.dynamicPeriodsFilters?.forEach(f => f.periodParsingOptions = periodParsingOptions);
	}

	get hasDynamicPeriod() {
		return this.filters.some(f => f.dim == 'PX');
	}

	get periodParsingOptions() : PeriodParsingOptions {
		const options: PeriodParsingOptions = {};
		if (this.context.base_date) options.baseDate = new Date(this.context.base_date);
		return options;
	}

	render = () => html`
		${this.module.period_types?.length ? html`
			<div class="row">
				<og-tree-dropdown
					.caption="${msg('Period Type')}"
					class="dropdown-period-types"
					color="transparent"
					.value="${this.context.period_type ? [[this.context.period_type.toString(), 0, 1]] : null}"
					@change="${this._onPeriodTypeChange}">
				</og-tree-dropdown>
			</div>
		` : '' }

		${this.hasDynamicPeriod ? html`
			<div class="row">
				<div class="date-input">
					<div class="date-input-label">${msg('Base date')}</div>
					<og-calendar-dropdown
						cross
						buttonInput
						noCaret
						color="transparent"
						.placeholder="${msg('Today')}"
						.value="${this.context.base_date ? new Date(this.context.base_date) : null}"
						@change="${this._onPeriodBaseDateChange}">
					</og-calendar-dropdown>
				</div>
			</div>
		` : '' }

		${map(this.rows, (row, index) => html`
			<div class="row" data-index="${index}">
				${row.filter.dim == 'PX' ? html`
					<div class="dynamicperiod">
						<rp-filters-dynamicperiod
							.periodParsingOptions="${this.periodParsingOptions}"
							.value="${this.normalizeFilterItems(this.getFilterItems(row.filter))?.[row.itemIndex]?.[0]}"
							@change="${(e: events.ChangeEvent) => this._onDynamicFilterChange(e, row)}">
						</rp-filters-dynamicperiod>

						<og-button
							class="remove"
							style="flex: 0 0 20px}"
							.icon="${{icon: 'trash', color: 'red'}}"
							color="tranparent"
							@click="${() => this.removeFilter(row)}">
						</og-button>
					</div>
				` : html`
					<og-tree-dropdown
						color="transparent"
						.adapter="${this.getAdapter(row.filter.dim as PeriodDimensionType)}"
						.value="${this.normalizeFilterItems(row.filter.items as ContextFilterItem[]|string[])}"
						@change="${(e: TreeEvent) => this._onFilterChange(e, row, index)}"
						cross>
					</og-tree-dropdown>

					<og-button
						class="remove"
						style="flex: 0 0 20px; display: ${row.filter?.items?.length ? 'none' : 'block'}"
						.icon="${{icon: 'trash', color: 'red'}}"
						color="tranparent"
						@click="${() => this.removeFilter(row)}">
					</og-button>
				`}
			</div>
		`)}

		${this.module?.dimensions ? html`
			<div class="row" style="display: block; margin-bottom: 0">
				<og-button
					caret="down"
					class="add"
					color="transparent"
					@click="${() => this.openAddMenu()}">
					<div slot="content">+ ${msg('Add period filter')}</div>
				</og-button>
			</div>
		` : null}

		<og-tree-menu ${ref(this.addMenuRef)}
			color="transparent"
			style="display: none"
			maxHeight="200"
			@select="${(e: TreeEvent) => this.addFilter(e.node.key as string)}">
		</og-tree-menu>
	`;

}
